import {
  ArtifactStateMark,
  Button,
  DayGroup,
  EmptyState,
  Input,
  Notice,
  PageFrame,
  ProblemBox,
  RowCell,
  RowsGrid,
  Select,
  TextLink,
} from "@don/design-system";
import type * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { DOWNLOAD_BUTTON, GettingStartedLink } from "../shell/recorder-links.js";

import { COLUMNS } from "./columns.js";
import { LibraryRowView } from "./LibraryRowView.js";
import {
  DEFAULT_FILTERS,
  applyFilters,
  cleared,
  peopleIn,
  type DateWindow,
  type LibraryFilters,
} from "./filters.js";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_PLACEHOLDER,
  corpusAnswer,
  corpusStanding,
  noMatchLine,
  normaliseQuery,
  searchNotice,
  type CorpusReply,
  type CorpusStanding,
  type SearchSource,
} from "./search.js";
import {
  NO_RENEWALS,
  atRisk,
  batchSettled,
  failedNotice,
  refusedNotice,
  renewalFor,
  renewalNotices,
  renewedIn,
  type RenewalStates,
} from "./retention.js";
import { groupByDay, type LibraryRow } from "./row.js";

const DATE_WINDOWS: { label: string; value: DateWindow }[] = [
  { label: "Any date", value: "all" },
  { label: "Last 30 days", value: "month" },
  { label: "This week", value: "week" },
  { label: "Today", value: "today" },
];

/** A notice sits 14 below the toolbar or the notice above it (variant E `ve-under`). */
const UNDER: React.CSSProperties = { marginTop: 14 };

/** A session's name inside a notice sentence: weight 600 (variant E `.ve-note-t b`). */
function Named({ children }: { children: React.ReactNode }) {
  return <strong style={{ fontWeight: "var(--type-body-weight-current)" }}>{children}</strong>;
}

/**
 * What happened to the renewals that did not renew anything (screen catalog
 * `sessions-renewal-refused`, `sessions-renewal-failed`), under the toolbar as `Notice`s:
 * nothing floats but menus and dialogs (design-spec §4 "Notice").
 *
 * **Shown once the batch has settled.** `Renew all (n)` fires n renewals that settle in
 * whatever order the network decides; the refused notice opens with how many renewed, which is
 * not known until the last one answers. With nothing in flight it shows at once.
 *
 * - **Refused** (info, dismiss ×): each refusal named, which {@link refusedNotice} words. The
 *   refused row itself reads `Video deleted` (`markerFor`).
 * - **Failed** (failed, `Try again`): one sentence for the whole set ({@link failedNotice});
 *   `Try again` asks again for exactly the failed sessions. The markers have not moved.
 *
 * Refusals and failures are the per-session states `App` holds (`renewalNotices`), so a
 * refusal settled first is not erased by a later success.
 */
function RenewalNotices({
  renewals,
  rows,
  renewed,
  nowMs,
  onRetry,
  onDismiss,
}: {
  renewals: RenewalStates;
  rows: readonly LibraryRow[];
  renewed: number;
  nowMs: number;
  onRetry?: (sessionIds: readonly string[]) => void;
  onDismiss: () => void;
}) {
  const { refusals, failed } = renewalNotices(renewals, rows);
  const refused = refusals.length > 0 ? refusedNotice(refusals, failed.length > 0 ? 0 : renewed, nowMs) : null;
  const failure = failed.length > 0 ? failedNotice(failed, renewed) : null;
  return (
    <>
      {refused && (
        <div data-renewal-notice="refused" style={UNDER}>
          <Notice tone="info" onClose={onDismiss}>
            {refused.lead && `${refused.lead} `}
            {refused.items.map((item, index) => (
              <span key={item.row.sessionId} data-renewal-notice-for={item.row.sessionId}>
                {index > 0 && " "}
                <Named>{item.name}</Named>
                {item.rest}
              </span>
            ))}
          </Notice>
        </div>
      )}
      {failure && (
        <div data-renewal-notice="failed" style={UNDER}>
          <Notice
            tone="failed"
            action={
              onRetry === undefined
                ? undefined
                : { children: "Try again", onClick: () => onRetry(failed.map((row) => row.sessionId)) }
            }
          >
            <Named>{failure.heading}</Named> {failure.body}
          </Notice>
        </div>
      )}
    </>
  );
}

/** The session the owner just deleted (catalog `sessions-deleted-notice`): `Deleted **title**.` */
function DeletedNotice({ deleted, onDismiss }: { deleted: string | null; onDismiss?: () => void }) {
  if (deleted === null) return null;
  return (
    <div data-deleted-notice="" style={UNDER}>
      <Notice tone="info" onClose={onDismiss}>
        Deleted <Named>{deleted}</Named>.
      </Notice>
    </div>
  );
}

/**
 * Where the library read is (issue 197): not answered yet, answered, or failed. Only `read`
 * says anything about what is in the library; the other two say nothing about it.
 */
export type LibraryRead = "reading" | "read" | "failed";

/**
 * The read failed (catalog `sessions-read-failed`, issue 90's distinction kept): the red-edged
 * box under the toolbar and nothing else. An empty layout here would tell the owner nothing
 * has been recorded, which the Console does not know.
 */
function UnreadableBox({ onRetry }: { onRetry?: () => void }) {
  return (
    <div data-library-unreadable="" style={UNDER}>
      <ProblemBox wide onRetry={onRetry}>
        <Named>Couldn&apos;t load your sessions.</Named> The server didn&apos;t answer; nothing was
        changed.
      </ProblemBox>
    </div>
  );
}

/**
 * The library read and holds nothing (catalog `sessions-empty`, ux-spec decision 23): the
 * title and two actions, no sentence about why (P21), no checklist and no counts (P1).
 * `Invite someone` only where `onInvite` is given, which `App` does only once the hidden
 * screens are revealed (P19), since it leads to People. `Download the recorder` goes to the
 * landing site's platform redirect, with `Getting started` under it (issue 260,
 * `shell/recorder-links.tsx`).
 */
function EmptyLibrary({ onInvite }: { onInvite?: () => void }) {
  return (
    <div data-library-empty="">
      <EmptyState
        title="No sessions yet"
        primary={onInvite === undefined ? undefined : { children: "Invite someone", onClick: onInvite, "data-library-invite": "" }}
        secondary={{ ...DOWNLOAD_BUTTON, "data-library-download": "" }}
        secondaryBelow={<GettingStartedLink />}
      />
    </div>
  );
}

/**
 * The column header (design-spec §4 "Column header", D36): one line on the same grid as every
 * row, 14/500 ink-2, padding 16 16 6. `aria-hidden`, because each cell carries its own title;
 * the labels are for the eye lining values up, not a second reading of every row.
 */
function ColumnHeader() {
  return (
    <RowsGrid kind="header" data-column-header="" aria-hidden="true">
      {COLUMNS.map((column) => (
        <RowCell
          key={column.key}
          data-column-label={column.key}
          lead={column.key === "title"}
          align={column.align}
          inset={column.inset}
          // The header's Session label keeps the header's type, not the title's.
          style={column.key === "title" ? HEADER_LEAD : undefined}
        >
          {column.label}
        </RowCell>
      ))}
    </RowsGrid>
  );
}

const HEADER_LEAD = {
  fontSize: "var(--type-small-size)",
  lineHeight: "var(--type-small-line)",
  fontWeight: "var(--type-small-weight-strong)",
  letterSpacing: "var(--type-small-tracking)",
  color: "var(--ink-2)",
} as const;

/**
 * What the corpus is doing, while it is doing something other than answering.
 *
 * **It exists so that `NoMatch` stops being shown for two things it is false of.** `No
 * session matches this filter` is a finished statement, and it was on screen for the whole of
 * every round trip — measured at every sample from the second character of a word to ~160 ms
 * past the last keystroke — and for the whole of every failed request, with `Clear the
 * filter` beside it offering to throw the unanswered query away.
 *
 * `role="status"` rather than a bare line, because the row set changes after typing has
 * stopped: a sighted owner sees the list settle and a screen-reader user was told nothing at
 * all. It is polite, so it waits for a pause rather than interrupting the keystroke.
 */
function CorpusNotice({ standing, onRetry }: { standing: CorpusStanding; onRetry: () => void }) {
  const line = searchNotice(standing);
  if (line === null) return null;
  // Under the toolbar (variant E `ve-under`, 14 below it).
  if (standing === "unreachable") {
    // A reduced mode, not a failure (design-spec §4 "Exceptions"): the info Notice, its ring
    // in ink-2, and `Try again`, which asks the corpus the same query again. The Notice is
    // itself the `role="status"` region.
    return (
      <div data-library-search-notice={standing} style={{ marginTop: 14 }}>
        <Notice tone="info" action={{ children: "Try again", onClick: onRetry }}>
          {line}
        </Notice>
      </div>
    );
  }
  return (
    <div
      data-library-search-notice={standing}
      role="status"
      style={{ marginTop: 14, paddingTop: 14, color: "var(--ink-2)" }}
    >
      <ArtifactStateMark state="pending">{line}</ArtifactStateMark>
    </div>
  );
}

/**
 * The filtered-to-nothing screen: the owner's own query or filter, and the way back from it.
 *
 * A query that matched nothing gets the catalog's sentence and `Clear search`, but only when
 * the query is what emptied the list: with a date window that leaves nothing on its own, "No
 * session mentions ..." would be false of rows the query never saw. The filter case keeps its
 * own sentence and `Clear the filter`, which clears everything that narrows the list.
 */
function NoMatch({ query, onClear }: { query: string | null; onClear: () => void }) {
  return (
    <div
      data-library-no-match=""
      style={{
        paddingTop: "var(--space-35)",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--space-1)",
      }}
    >
      <p style={{ margin: 0, color: "var(--ink-1)" }}>
        {query === null ? "No session matches this filter" : noMatchLine(query)}
      </p>
      {/* The only way back from this screen, so a standalone accent link (E, screen-catalog),
          not a quiet button (design-spec §4 keeps those to inline actions). */}
      <TextLink onClick={onClear}>{query === null ? "Clear the filter" : "Clear search"}</TextLink>
    </div>
  );
}

export function LibraryScreen({
  rows,
  nowMs,
  onOpen,
  onRenew,
  renewals = NO_RENEWALS,
  deleted = null,
  onDismissDeleted,
  searchSource = null,
  read = "read",
  onRetryRead,
  onInvite,
}: {
  rows: readonly LibraryRow[];
  nowMs: number;
  onOpen: (row: LibraryRow) => void;
  /**
   * Renew one session, or `undefined` where there is nowhere to send a renewal.
   *
   * The same function the session detail's control is (issue 30): `App` owns it, so the
   * red marker's Renew and the detail's Renew are two call sites of one path rather than two
   * implementations that agree.
   */
  onRenew?: (sessionId: string) => void;
  /**
   * What happened to each session's renewal, so the screen can say which did not happen.
   *
   * Keyed by session: a batch settles in whatever order the network decides, and one value
   * for all of them let a later success erase an earlier refusal.
   */
  renewals?: RenewalStates;
  /** The name of the session the last owner delete removed, or `null` where none has happened. */
  deleted?: string | null;
  /** Put the deleted notice away (its ×). */
  onDismissDeleted?: () => void;
  /** Where a corpus answer comes from, or `null` where there is no corpus to ask. */
  searchSource?: SearchSource | null;
  /**
   * Where the library read is (issue 197). `failed`: `rows` is not the server's answer, so
   * only the problem box is drawn (issue 90). `reading`: nothing is claimed yet.
   */
  read?: LibraryRead;
  /** Read the library again (the problem box's `Try again`). */
  onRetryRead?: () => void;
  /**
   * The empty library's `Invite someone`, or `undefined` where it is not offered: it leads to
   * People, one of the screens the deployed Console hides (issue 118, story 19). Search needs
   * no switch: with no corpus source no search field is drawn at all (issue 194, story 57).
   */
  onInvite?: () => void;
}) {
  const [filters, setFilters] = useState<LibraryFilters>(DEFAULT_FILTERS);
  // The read failed: only the problem box is true, so the toolbar is drawn disabled.
  const unanswered = read === "failed";
  // Where focus goes when the filter is cleared. A ref on the toolbar rather than on the
  // box itself: `Input` is a plain function component and forwards no ref, so the box is
  // reached by asking the element that contains it. Issue 25's Decision 3 keeps
  // `packages/design-system` untouched, and adding `forwardRef` to a port pinned against
  // the shipped bundle is that package's own change to make.
  const toolbar = useRef<HTMLDivElement>(null);
  const [reply, setReply] = useState<CorpusReply | null>(null);
  // Bumped by the unreachable notice's `Try again`, so the same query is asked once more.
  const [attempt, setAttempt] = useState(0);
  const set = <K extends keyof LibraryFilters>(key: K, value: LibraryFilters[K]) =>
    setFilters((current) => ({ ...current, [key]: value }));

  // Ask the corpus what this query matches. **Nothing here guards against a late answer**,
  // and that is deliberate: `applyFilters` only applies an answer to the query it was asked
  // for, so an answer that arrives after the owner has typed past it narrows nothing. The
  // race is closed by the data rather than by a counter in this component, which is the one
  // shape of it a test without a browser can assert.
  //
  // Two things are ordinary here rather than exceptional, and both are states of the screen
  // above rather than branches of this effect: for the whole of the wait the corpus has said
  // nothing about this query, and a source can reject. The rejection handler is the second
  // argument to `then` rather than a `.catch`, so it cannot catch a failure of `setReply`.
  useEffect(() => {
    const query = normaliseQuery(filters.query);
    if (searchSource === null || query.length === 0) {
      setReply(null);
      return;
    }
    let live = true;
    // Trailing: the ask is for the word, not for each of its prefixes.
    const timer = setTimeout(() => {
      void searchSource(query).then(
        (sessionIds) => {
          if (live) setReply({ state: "answered", query, sessionIds });
        },
        () => {
          if (live) setReply({ state: "unreachable", query });
        },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [searchSource, filters.query, attempt]);

  // With no source there is no field (story 57), so no query narrows anything, even one typed
  // before a source went away.
  const active = useMemo(
    () => (searchSource === null ? { ...filters, query: "" } : filters),
    [filters, searchSource],
  );
  const standing = corpusStanding(reply, active.query, searchSource !== null);
  // Memoised for the same reason the row set below is: it is a fresh object on every render
  // otherwise, and `applyFilters` would re-run on every keystroke of a filter it does not
  // read.
  const answer = useMemo(() => corpusAnswer(reply), [reply]);
  const visible = useMemo(
    () => applyFilters(nowMs, active, rows, answer),
    [nowMs, active, rows, answer],
  );
  // Whether the query is what emptied the list: the person and date alone leave rows.
  const queryEmptied =
    normaliseQuery(active.query).length > 0 &&
    visible.length === 0 &&
    applyFilters(nowMs, { ...active, query: "" }, rows).length > 0;
  const focusSearch = () => toolbar.current?.querySelector("input")?.focus();
  // Which row's red marker is open: one at a time, as variant E toggles it.
  const [open, setOpen] = useState<string | null>(null);
  // The sessions the last renewal from this screen asked about, so the refused notice can say
  // how many renewed once they have all answered.
  const [batch, setBatch] = useState<readonly string[]>([]);
  // The renewal states the owner dismissed the notice on; a new outcome is a new object.
  const [dismissed, setDismissed] = useState<RenewalStates | null>(null);
  // **`Renew all (n)` counts the rows in front of the owner** (catalog `sessions-filtered`:
  // Kristine's two rows read `Renew all (1)`), soon plus urgent, and renews exactly that set.
  const risky = useMemo(() => atRisk(nowMs, visible, renewals), [nowMs, visible, renewals]);
  const renew =
    onRenew === undefined
      ? undefined
      : (sessionIds: readonly string[]) => {
          setBatch(sessionIds);
          setDismissed(null);
          for (const id of sessionIds) onRenew(id);
        };
  const settled = batchSettled(batch, renewals);

  // **One order, newest first, cut into day groups** (issue 193): the sort control is gone.
  const groups = useMemo(() => groupByDay(nowMs, visible), [nowMs, visible]);

  // **A library that read and holds nothing is one title and two actions** (catalog
  // `sessions-empty`): no toolbar, no header. The deleted notice stays, since deleting the last
  // session is how a library becomes empty.
  if (read === "read" && rows.length === 0) {
    return (
      <div data-library="" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <PageFrame variant="wide">
          <DeletedNotice deleted={deleted} onDismiss={onDismissDeleted} />
          <EmptyLibrary onInvite={onInvite} />
        </PageFrame>
      </div>
    );
  }

  // The page scrolls as one, on the wide frame (Sessions only, D36): the toolbar, the notices
  // under it, the column header and the day groups sit on the sheet, and each group's label
  // sits in the margin column outside it.
  return (
    <div data-library="" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <PageFrame variant="wide">
        <div
          ref={toolbar}
          data-library-toolbar=""
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-15)",
          }}
        >
          {/* One field, and it narrows the list in front of the owner. There is no results
              screen to navigate to, so `route.ts` has no search route to reach. Drawn only
              with a corpus source (story 57): `Search sessions` promises what was said, and
              with nothing to ask there is no field. The filters stay either way. */}
          {searchSource !== null ? (
            <Input
              value={filters.query}
              onChange={(event) => set("query", event.target.value)}
              placeholder={SEARCH_PLACEHOLDER}
              aria-label={SEARCH_PLACEHOLDER}
              data-library-search=""
              search
              disabled={unanswered}
              onClear={
                filters.query.length > 0
                  ? () => {
                      set("query", "");
                      focusSearch();
                    }
                  : undefined
              }
              clearLabel="Clear search text"
              style={{ flex: 1 }}
            />
          ) : (
            <span style={{ flex: 1 }} />
          )}
          {/* A chosen value wears pale lavender with a × (design-spec §4 "Filter select"). */}
          <Select
            aria-label="Person"
            disabled={unanswered}
            value={filters.person ?? ""}
            onChange={(event) => set("person", event.target.value || null)}
            onClear={filters.person === null ? undefined : () => set("person", null)}
            clearLabel="Clear person filter"
            options={[
              { label: "Everyone", value: "" },
              ...peopleIn(rows).map((person) => ({ label: person, value: person })),
            ]}
          />
          <Select
            aria-label="Date"
            disabled={unanswered}
            value={filters.dateWindow}
            onChange={(event) => set("dateWindow", event.target.value as DateWindow)}
            onClear={filters.dateWindow === "all" ? undefined : () => set("dateWindow", "all")}
            clearLabel="Clear date filter"
            options={DATE_WINDOWS}
          />
          {/* Secondary, never accent (design-spec §5): an ordinary action, and offered only
              where a renewal has somewhere to go and something is at risk. A failed read has
              no rows to be at risk. */}
          {renew !== undefined && risky.length > 0 && !unanswered && (
            <Button
              variant="secondary"
              data-renew-all={risky.length}
              onClick={() => renew(risky.map((row) => row.sessionId))}
            >
              {`Renew all (${risky.length})`}
            </Button>
          )}
        </div>
        {/* What the corpus is doing, under the toolbar (variant E). */}
        <CorpusNotice
          standing={standing}
          onRetry={() => {
            setReply(null);
            setAttempt((n) => n + 1);
            // `Try again` unmounts with the notice (the asking line replaces it), so focus goes
            // back to the field the query is in rather than falling to `BODY`.
            focusSearch();
          }}
        />

        {unanswered && <UnreadableBox onRetry={onRetryRead} />}
        {!unanswered && settled && renewals !== dismissed && (
          <RenewalNotices
            renewals={renewals}
            rows={rows}
            renewed={renewedIn(batch, renewals)}
            nowMs={nowMs}
            onRetry={renew}
            onDismiss={() => setDismissed(renewals)}
          />
        )}
        {!unanswered && <DeletedNotice deleted={deleted} onDismiss={onDismissDeleted} />}

        <div aria-busy={standing === "asking" || read === "reading"}>
          {/* Labels for rows, so not drawn over a search or filter that left none (variant E
              `sessions-search-no-match`, `-asking`), nor over a read that has not answered or
              failed (catalog `sessions-read-failed`). */}
          {!unanswered && visible.length > 0 && <ColumnHeader />}
          {/* While the corpus is asked, the rows the strings already match stay listed under
              the notice: they are true whatever the corpus comes back with. */}
          {unanswered || rows.length === 0 ? null : visible.length === 0 ? (
            // **`NoMatch` is for a search that finished and found nothing, and only that.**
            // While the corpus is being asked, or after the ask failed, the notice above is
            // the whole of what this screen can honestly say, and `Clear search`, which
            // throws the owner's unanswered query away, must not be the control offered for a
            // question nobody has answered yet.
            standing === "asking" || standing === "unreachable" ? null : (
              <NoMatch
                query={queryEmptied ? active.query : null}
                onClear={() => {
                  // `Clear search` clears the query and keeps the person and date; `Clear the
                  // filter` clears everything that narrows the list.
                  setFilters(queryEmptied ? (current) => ({ ...current, query: "" }) : cleared);
                  // **The control that removes the screen has to say where focus went.**
                  // `NoMatch` unmounts the moment the rows come back, and a keyboard or
                  // screen-reader user was left on `BODY`, nothing announced, and the next
                  // Tab starting again from the top of the document. The field they typed in
                  // is where they were, so it is where they are put back (with no field, the
                  // filter case, nothing takes focus here).
                  focusSearch();
                }}
              />
            )
          ) : (
            groups.map((group, index) => (
              <DayGroup key={group.label} label={group.label} first={index === 0}>
                {group.rows.map((row) => (
                  <LibraryRowView
                    key={row.sessionId}
                    row={row}
                    nowMs={nowMs}
                    onOpen={onOpen}
                    onRenew={
                      renew === undefined
                        ? undefined
                        : (id) => {
                            // The box closes on Renew: what follows is the marker moving, or a
                            // notice under the toolbar.
                            setOpen(null);
                            renew([id]);
                          }
                    }
                    renewal={renewalFor(renewals, row.sessionId)}
                    expanded={open === row.sessionId}
                    onToggle={() => setOpen((current) => (current === row.sessionId ? null : row.sessionId))}
                    query={active.query}
                  />
                ))}
              </DayGroup>
            ))
          )}
        </div>

      </PageFrame>
    </div>
  );
}
